import React from "react";
import { FiZap, FiCode, FiUsers, FiGlobe } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSearch,
  LuCircleCheck,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuCpu,
  LuClipboardList,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

export const reactNativeBenefits = [
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Native Look & Feel",
    desc: "Uses native components instead of web views, allowing your app to provide a truly native user experience.",
  },
  {
    icon: <FiCode className="w-8 h-8" />,
    title: "Code Reusability",
    desc: "Up to 80-90% of the code can be shared between iOS and Android, significantly reducing development time.",
  },
  {
    icon: <FiUsers className="w-8 h-8" />,
    title: "Large Community",
    desc: "Supported by Meta (Facebook) and a massive developer community, ensuring plenty of support and libraries.",
  },
];

export const reactNativeServices = [
  {
    icon: <FiGlobe size={36} />,
    title: "Native Performance Apps",
    description: "Building high-performance mobile applications using React Native for a seamless experience.",
    image: "/images/MobileAppDevelopment/React_Native_Apps.png",
    link: "/react-native-mobile-app-services",
    points: ["Native Component Rendering", "High FPS & Smooth Animations", "JavaScript / TypeScript Development", "Cross-Platform Performance Architecture"],
  },
  {
    icon: <FiCode size={36} />,
    title: "React Native Migration",
    description: "Helping you migrate your existing applications to React Native for better performance and maintainability.",
    image: "/images/MobileAppDevelopment/App-Migration.png",
    link: "/react-native-migration-services",
    points: ["Legacy Codebase Auditing", "Seamless Platform Re-platforming", "Native Bridge Implementations", "Data Preservation & Testing"],
  },
];

export const reactNativeProcessSteps = [
  {
    step: "01",
    title: "Requirement Gathering",
    description: "We work with you to understand your goals and identify the key features for your React Native app.",
    points: ["Project scope definition", "User story mapping", "Feature planning"],
  },
  {
    step: "02",
    title: "React Architecture",
    description: "Designing a scalable and maintainable architecture for your application using React principles.",
    points: ["Component architecture", "State management", "Navigation layout"],
  },
  {
    step: "03",
    title: "Development & Testing",
    description: "Developing the app and conducting extensive testing across multiple devices.",
    points: ["Coding with React Native", "Continuous testing", "QA & bug fixing"],
  },
];

export const ReactNativeAppHeroData = {
  backgroundImage: "/images/serviceBanners/ReactNativeBanner.webp",
  whiteImage: "/images/serviceBanners/ReactnativeW.webp",
  subTitle: "React Native App Development Services",
  title: (
    <>
      Native Power with <br /> Exceptional <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Efficiency</span>
    </>
  ),
  description:
    "We build high performance React Native applications that deliver near native experience using JavaScript and React, enabling fast, scalable, and cost efficient mobile solutions for both iOS and Android platforms.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Get Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Native Componentry",
      desc: "Render real native UI components for smooth performance.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Code Reuse (90%)",
      desc: "Share most code between iOS and Android for faster development.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Fast Refresh",
      desc: "Instant live updates for faster testing and development speed.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Meta-Backed Tech",
      desc: "Strong long-term support and stable React Native ecosystem.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "8%",
      right: "16%",
      containerSize: 70,
      iconSize: 50,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

// 5. Stats specific to React Native installations
export const reactNativeStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 6. Why Choose Us block mapping for React Native features
export const reactNativeSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "React Native Architectures for Business Performance",
  buildCards: [
    {
      title: "Cross-Platform Ecosystems",
      desc: "Single codebase rendering native UI flawlessly across iOS and Android systems.",
      icon: LuSmartphone,
      colorClass: "text-[#61dafb]",
      bgClass: "bg-[#61dafb]/10",
      borderClass: "border-[#61dafb]/20 hover:border-[#61dafb]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(97,218,251,0.25)]",
    },
    {
      title: "Enterprise Solutions",
      desc: "Secure and scalable React Native architecture for sophisticated business systems.",
      icon: LuBuilding2,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Integrations",
      desc: "Fast checkout flows with a smooth and secure shopping experience for users.",
      icon: LuShoppingBag,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Native Bridge Customization",
      desc: "Access device hardware using high-performance Swift, Java, and Objective-C bridges.",
      icon: LuCpu,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "State Management Systems",
      desc: "Efficient data handling and predictable application flow using Redux or Context API.",
      icon: LuSettings,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Performance Optimization",
      desc: "Improve app speed, responsiveness, and memory usage for a fluid user experience.",
      icon: FiZap,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR DEVELOPMENT PROCESS",
  processTitle: "React Native Development Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery",
      description: "Define project scope, features, and user experience layout blueprints.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Architecture Planning",
      description: "Design scalable state management and modular component structure.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Development",
      description: "Build cross-platform React Native applications using optimized codebase.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Testing",
      description: "Ensure performance, stability, and compatibility across various mobile devices.",
      icon: LuBug,
    },
    {
      step: "05",
      title: "Deployment",
      description: "Release apps on App Store and Play Store with efficient CI/CD pipelines.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Maintenance",
      description: "Provide updates, fixes, and continuous performance improvements post-launch.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE REACT NATIVE?",
  benefitsTitle: "Shared Codebase Advantage in React Native",
  benefits: [
    "Native UI components instead of web views",
    "High code reuse across iOS and Android (90%)",
    "Direct ecosystem support from Meta",
    "Fast state updates and Context API logic",
    "Simplified debugging and troubleshooting process",
    "Reduced long-term maintenance costs",
    "Lower overall product lifecycle costs",
  ],
  logoImage: "/images/serviceBanners/ReactNativeBanner.webp",
  whiteLogoImage: "/images/serviceBanners/ReactnativeW.webp",
};

// 7. Portfolio projects mapped over for React Native layouts
export const reactNativePortfolioData = {
  subtitle: "OUR WORK",
  title: "React Native Deployments Making An Impact",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "ShopVibe",
      badge: "E-Commerce Mobile",
      badgeColor: "bg-[#61dafb]/10 text-[#61dafb] border-[#61dafb]/20",
      desc: "Frictionless shopping terminal implementing quick search rendering and local database storage features.",
      downloads: "200K+",
      rating: "4.8",
      image: "/images/MobileAppDevelopment/React_Native_Apps.png",
    },
    {
      title: "FinPulse",
      badge: "Financial Hub",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Highly custom dashboard rendering dynamic charting layouts safely via clean state mechanisms.",
      downloads: "100K+",
      rating: "4.9",
      image: "/images/MobileAppDevelopment/App-Migration.png",
    },
  ],
};

// 8. Frameworks used inside the engineering setup
export const reactNativeTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "React Native", icon: "ReactNative" },
    { name: "JavaScript", icon: "JavaScript" },
    { name: "TypeScript", icon: "TypeScript" },
    { name: "Redux Toolkit", icon: "Redux" },
    { name: "Axios", icon: "Axios" },
    { name: "Lottie Files", icon: "Lottie" },
  ],
  trustTitle: "TRUSTED BY LEADING BUSINESSES",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 9. About overview matching the exact standard text parameters
export const reactNativeAboutData = [
  {
    title: "React Native App Development Company for Scalable Mobile Experiences",
    content: (
      <>
        Modern businesses require mobile applications that deliver strong performance across platforms without increasing development complexity. A trusted{" "}
        <b className="text-text-primary font-semibold">react native app development company</b> helps organizations create scalable applications that improve accessibility,
        customer engagement, and long-term operational efficiency. Many businesses struggle with maintaining separate mobile ecosystems for different operating systems, which
        increases cost and slows product updates. At <b className="text-text-primary font-semibold">Tycho Technologies</b>, React Native solutions are developed around business
        goals, usability, and scalability to help organizations create reliable digital experiences that support growth.
      </>
    ),
  },
  {
    title: "Cross Platform Mobile App Development for Better Efficiency",
    content: (
      <>
        Building applications that work smoothly across multiple devices has become essential for businesses focused on digital growth. Reliable{" "}
        <b className="text-text-primary font-semibold">cross platform mobile app development</b> helps companies improve reach while maintaining a consistent user experience across
        operating systems. Businesses investing in <b className="text-text-primary font-semibold">react native mobile app development</b> often benefit from stronger flexibility
        because applications can adapt more efficiently to evolving customer expectations and market needs. Tycho Technologies focuses on practical React Native frameworks designed
        to improve performance while supporting long-term digital transformation.
      </>
    ),
  },
  {
    title: "Hire React Native Developers for Smarter Mobile Solutions",
    content: (
      <>
        Growing businesses often require mobile applications that remain responsive and adaptable as customer behavior changes. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire react native developers</b> usually prioritize maintainability, scalability, and faster deployment for long-term
        success. Organizations aiming to strengthen digital ecosystems frequently align mobile innovation with{" "}
        <a href="https://tychotechnologies.com/pwa-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          progressive web app development
        </a>{" "}
        to improve accessibility and create connected user experiences across multiple platforms.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a React Native App Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">react native app development company in india</b> continues to grow as businesses seek scalable and
        cost-effective mobile development frameworks. Companies investing in <b className="text-text-primary font-semibold">react native development services india</b> often
        prioritize performance, flexibility, and smoother user experiences. Businesses partnering with Tycho Technologies gain access to customized React Native strategies designed
        around measurable business goals and technical requirements. Many organizations also strengthen mobile ecosystems through{" "}
        <a href="https://tychotechnologies.com/ionic-app-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          ionic app development company
        </a>{" "}
        solutions to support connected and future-ready digital experiences.
      </>
    ),
  },
];

// 10. Existing FAQs preserved intact
export const reactNativeFaqs = [
  {
    q: "Why should I hire React Native developers in India?",
    a: "Hiring React Native developers in India gives you access to skilled professionals who can build high-quality, cross-platform mobile apps at a cost-effective rate. Indian developers are experienced in creating fast, responsive apps for both Android and iOS using a single codebase.",
  },
  {
    q: "What do React Native development services include?",
    a: "React Native development services typically include custom mobile app development, UI/UX design, API integration, app testing, deployment, and ongoing support. These services help businesses launch scalable and performance-driven cross-platform apps efficiently.",
  },
  {
    q: "What is React Native development and why is it popular?",
    a: "React Native development is the process of building mobile apps using the React Native framework, which allows a single codebase to run on both Android and iOS. It’s popular because it reduces development time, ensures consistent user experience, and lowers overall costs.",
  },
];

// 11. Fallback array export to prevent any legacy TechStacks.jsx build crashes
export const reactNativeTechStack = ["React Native", "JavaScript", "TypeScript", "Redux", "Context API", "Axios", "Lottie"];
